import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Code2, ReceiptText, Wrench, GitCompareArrows, Clock } from 'lucide-react';
import SEO from '../../../components/SEO';
import StructuredData from '../../../components/StructuredData';
import LandingNavBar from '../../../components/LandingNavBar';
import LandingFooter from '../../../components/LandingFooter';
import beds24Articles, { CATEGORY_META, CATEGORY_ORDER } from './beds24Content';

const SITE = 'https://staymii.com';

const CATEGORY_ICONS = {
  Core: BookOpen,
  API: Code2,
  'e-Invoice': ReceiptText,
  Tutorial: Wrench,
  Comparison: GitCompareArrows,
};

const Beds24Hub = () => {
  const grouped = useMemo(() => {
    const map = {};
    beds24Articles.forEach(a => { (map[a.category] = map[a.category] || []).push(a); });
    return map;
  }, []);

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'StayMii Beds24 Knowledge Centre',
    itemListElement: beds24Articles.map((a, i) => ({
      '@type': 'ListItem', position: i + 1, name: a.title, url: `${SITE}/beds24/${a.slug}`,
    })),
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE + '/' },
      { '@type': 'ListItem', position: 2, name: 'Beds24 Knowledge Centre', item: SITE + '/beds24' },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="beds24-hub-page">
      <LandingNavBar />
      <SEO
        title="Beds24 Knowledge Centre — Guides, API Docs & Malaysia e-Invoice"
        description="The complete StayMii Beds24 resource centre: setup guides, API documentation, LHDN e-Invoice automation, tutorials and comparisons for Malaysian hotels and short-term rental operators."
        keywords="beds24, beds24 malaysia, beds24 api, beds24 guide, beds24 e-invoice, beds24 tutorial, beds24 review"
        canonicalUrl="/beds24"
      />
      <StructuredData id="beds24-hub-itemlist" schema={itemListSchema} />
      <StructuredData id="beds24-hub-breadcrumb" schema={breadcrumbSchema} />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-[#0B1121] relative overflow-hidden" data-testid="beds24-hub-hero">
        <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #2563EB 0, transparent 40%), radial-gradient(circle at 80% 30%, #1D4ED8 0, transparent 40%)' }} />
        <div className="max-w-6xl mx-auto px-4 relative">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium border border-white/15 text-white/70 mb-5">StayMii Knowledge Centre</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5 max-w-4xl">The Complete Beds24 Resource for Malaysia</h1>
          <p className="text-white/60 text-lg max-w-2xl mb-8">Everything you need to master Beds24 — from first setup and API integration to automating LHDN e-Invoice compliance. Written and maintained by the StayMii integration team.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/beds24/what-is-beds24" className="px-7 py-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-semibold transition-colors inline-flex items-center gap-2" data-testid="beds24-hub-start">Start with the basics <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/beds24/staymii-beds24-integration" className="px-7 py-3 bg-white/10 hover:bg-white/20 text-white border border-white/15 rounded-xl font-semibold transition-colors" data-testid="beds24-hub-integration">StayMii + Beds24</Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <div className="max-w-6xl mx-auto px-4 py-14">
        {CATEGORY_ORDER.filter(c => grouped[c] && grouped[c].length).map((category) => {
          const meta = CATEGORY_META[category];
          const Icon = CATEGORY_ICONS[category] || BookOpen;
          return (
            <section key={category} className="mb-14" data-testid={`beds24-cat-${category.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <div className="flex items-center gap-3 mb-6">
                <span className={`h-11 w-11 rounded-xl flex items-center justify-center ${meta.tile}`}><Icon className="h-5 w-5" /></span>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{meta.title}</h2>
                  <p className="text-slate-500 text-sm">{meta.blurb}</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {grouped[category].map((a) => (
                  <Link key={a.slug} to={`/beds24/${a.slug}`} className="group flex flex-col bg-white border border-slate-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-md transition-all" data-testid={`beds24-card-${a.slug}`}>
                    <h3 className="font-semibold text-slate-900 group-hover:text-[#2563EB] transition-colors mb-2 leading-snug">{a.title}</h3>
                    <p className="text-slate-500 text-sm line-clamp-2 mb-4 flex-1">{a.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-xs flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {a.readTime}</span>
                      <span className="inline-flex items-center gap-1.5 text-[#2563EB] text-sm font-medium group-hover:gap-2.5 transition-all">Read <ArrowRight className="h-3.5 w-3.5" /></span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* CTA */}
      <section className="py-16 px-4 bg-[#0B1121]" data-testid="beds24-hub-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">Beds24 + StayMii = compliant, automated invoicing</h2>
          <p className="text-white/50 mb-8">Join Malaysian operators using StayMii to turn every Beds24 booking into an LHDN-validated e-invoice.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="px-8 py-3.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-medium transition-colors" data-testid="beds24-hub-cta-demo">Book a Demo</Link>
            <Link to="/integrations/beds24-e-invoice" className="px-8 py-3.5 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-xl font-medium transition-colors" data-testid="beds24-hub-cta-learn">Explore the Integration</Link>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default Beds24Hub;
